/**
 * StateEntry — Dynamic Default State Resolution (V102)
 *
 * Resolves the user's first available state dynamically.
 * NEVER hardcodes a state_id.
 *
 * Resolution order:
 *  1. URL param stateId (if already set)
 *  2. User's assigned state from /api/states/state-list (first returned)
 *  3. If no states available → /states/start onboarding
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Map, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client from "@/api/client";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function StateEntry() {
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);
  const [noStates,   setNoStates]   = useState(false);

  useEffect(() => {
    client.get("/states/state-list")
      .then(r => {
        const states = r.data?.states || [];
        if (states.length === 0) {
          setNoStates(true);
          setResolving(false);
          return;
        }
        // Use the first available state — no hardcoded ID
        const first = states[0];
        if (first.state_id) {
          setRedirectTo(`/states/${first.state_id}/overview`);
        } else {
          // State exists but has no ID (geography-only state) — go to overview with geography param
          setRedirectTo(`/states/overview`);
        }
        setResolving(false);
      })
      .catch(() => {
        // Network error — show onboarding
        setNoStates(true);
        setResolving(false);
      });
  }, []);

  if (resolving) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={20} className="animate-spin text-teal-500 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Resolving state context...</p>
        </div>
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (noStates) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-[#0F172A] w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-5">
            <Map size={24} className="text-teal-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">No State Assigned</h1>
          <p className="text-sm text-slate-500 mb-6">
            Your account has no state workspace configured.
            An admin must create and assign a state organization before you can access the States workspace.
          </p>
          <div className="space-y-2">
            <button onClick={() => navigate("/admin/orgs")}
              className="w-full py-2.5 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800"
              data-testid="admin-assign-state-btn">
              Admin: Assign State →
            </button>
            <button onClick={() => navigate("/dashboard")}
              className="w-full py-2.5 border border-slate-200 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-50">
              Return to Dashboard
            </button>
          </div>
          <p className="text-xs text-slate-400 italic mt-4">
            State workspaces are created by platform administrators via Admin → Organizations.
          </p>
        </div>
      </div>
    );
  }

  return null;
}
